import React, { useState, useEffect, useCallback } from 'react';
import { auth, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import Header from '../components/Header';

const FamilyPage = ({ navigateTo, currentPage }) => {
    const [familyData, setFamilyData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [newFamilyName, setNewFamilyName] = useState('');

    const fetchFamilyData = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const getFamilyData = httpsCallable(functions, 'getFamilyData');
            const result = await getFamilyData();
            setFamilyData(result.data);
        } catch (err) {
            console.error("Error fetching family data:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFamilyData();
    }, [fetchFamilyData]);

    const handleCreateFamily = async (e) => {
        e.preventDefault();
        if (!newFamilyName) return;
        setIsLoading(true);
        try {
            const createFamily = httpsCallable(functions, 'createFamily');
            await createFamily({ familyName: newFamilyName });
            fetchFamilyData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail) return;
        setIsLoading(true);
        try {
            const sendFamilyInvitation = httpsCallable(functions, 'sendFamilyInvitation');
            await sendFamilyInvitation({ toEmail: inviteEmail });
            setInviteEmail('');
            alert('Invitation sent!');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAcceptInvite = async (invitationId) => {
        setIsLoading(true);
        try {
            const acceptFamilyInvitation = httpsCallable(functions, 'acceptFamilyInvitation');
            await acceptFamilyInvitation({ invitationId });
            fetchFamilyData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8">
        <Header title="My Profile" navigateTo={navigateTo} currentPage={currentPage} />
        
            {isLoading ? <p>Loading family data...</p> : (
                <>
                    {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert"><p>{error}</p></div>}

                    {familyData?.pendingInvitations?.length > 0 && (
                        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
                            <h3 className="font-bold">You have pending invitations!</h3>
                            {familyData.pendingInvitations.map(invite => (
                                <div key={invite.id} className="flex justify-between items-center mt-2">
                                    <p>You've been invited to join the "{invite.familyName}" family by {invite.fromEmail}.</p>
                                    <button onClick={() => handleAcceptInvite(invite.id)} className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600">Accept</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {!familyData?.hasFamily ? (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-xl font-semibold mb-4">Create Your Family</h2>
                            <p className="text-gray-600 mb-4">You are not currently in a family. Create one to start sharing your budget.</p>
                            <form onSubmit={handleCreateFamily} className="flex items-center space-x-2">
                                <input type="text" value={newFamilyName} onChange={(e) => setNewFamilyName(e.target.value)} placeholder="Enter Family Name" className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm" required />
                                <button type="submit" className="px-5 py-2 font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">Create</button>
                            </form>
                        </div>
                    ) : (
                        <>
                            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                                <h2 className="text-2xl font-bold text-gray-800 mb-4">{familyData.familyName}</h2>
                                <h3 className="text-lg font-semibold mb-2">Members</h3>
                                <ul className="divide-y divide-gray-200">
                                    {familyData.members.map(member => (
                                        <li key={member.uid} className="py-3 flex justify-between items-center">
                                            <span>{member.displayName} ({member.email})</span>
                                            {member.uid === familyData.ownerId && <span className="text-xs font-semibold uppercase text-green-600 bg-green-100 px-2 py-1 rounded-full">Owner</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {familyData.ownerId === auth.currentUser.uid && (
                                <div className="bg-white p-6 rounded-lg shadow-md">
                                    <h3 className="text-lg font-semibold mb-2">Invite a New Member</h3>
                                    <form onSubmit={handleSendInvite} className="flex items-center space-x-2">
                                        <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Member's Email Address" className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm" required />
                                        <button type="submit" className="px-5 py-2 font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">Send Invite</button>
                                    </form>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default FamilyPage;